/**
 * 보안 메모 서비스
 * Firestore와의 CRUD 작업 처리
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { encryptData, decryptData } from './webCryptoService';

const COLLECTION_NAME = 'secureNotes';

/**
 * 메모 생성
 */
export async function createSecureNote(userId, noteData) {
  try {
    const { title, content, plainTextContent, tags, attachments, folderId = 'all-notes', ...rest } = noteData;
    
    // 민감한 데이터 암호화
    const encryptedTitle = await encryptData(title, userId);
    const encryptedContent = await encryptData(content, userId);
    const encryptedPlainText = await encryptData(plainTextContent, userId);
    const encryptedTags = tags ? await encryptData(JSON.stringify(tags), userId) : null;
    
    // 첨부파일 이름 암호화 (URL과 path는 암호화하지 않음)
    const encryptedAttachments = attachments && attachments.length > 0 
      ? await Promise.all(
          attachments.map(async (att) => ({
            ...att,
            name: await encryptData(att.name, userId)
          }))
        )
      : [];
    
    const noteDoc = {
      ...rest,
      userId,
      folderId,
      title: encryptedTitle,
      content: encryptedContent,
      plainTextContent: encryptedPlainText,
      tags: encryptedTags,
      attachments: encryptedAttachments,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastAccessedAt: serverTimestamp()
    };
    
    console.log('📄 Creating secure note with data:', {
      userId,
      hasTitle: !!noteDoc.title,
      hasContent: !!noteDoc.content,
      hasUserId: !!noteDoc.userId,
      attachmentCount: encryptedAttachments.length,
      restFields: Object.keys(rest),
      allFields: Object.keys(noteDoc)
    });
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), noteDoc);
    
    // 생성된 문서를 다시 가져와서 실제 타임스탬프 값 얻기
    const createdDoc = await getDoc(docRef);
    const createdData = createdDoc.data();
    
    // 생성된 메모를 복호화하여 반환
    const decryptedNote = await decryptNoteData(createdData, userId);
    return { id: docRef.id, ...decryptedNote };
  } catch (error) {
    console.error('Error creating secure note:', error);
    throw error;
  }
}

/**
 * 메모 메타데이터만 조회 (PIN 보호 확인용)
 */
export async function getSecureNoteMetadata(noteId, userId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, noteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Note not found');
    }
    
    const data = docSnap.data();
    
    // 권한 확인
    if (data.userId !== userId) {
      throw new Error('Unauthorized access');
    }
    
    // PIN 보호 관련 메타데이터만 반환 (내용은 복호화하지 않음)
    return {
      id: docSnap.id,
      isPinProtected: data.isPinProtected || false,
      pinHash: data.pinHash,
      pinSalt: data.pinSalt,
      userId: data.userId,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
  } catch (error) {
    console.error('Error getting secure note metadata:', error);
    throw error;
  }
}

/**
 * 메모 조회 (PIN 검증 후에만 복호화)
 */
export async function getSecureNote(noteId, userId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, noteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Note not found');
    }
    
    const data = docSnap.data();
    
    // 권한 확인
    if (data.userId !== userId) {
      throw new Error('Unauthorized access');
    }
    
    // 복호화
    const decryptedNote = await decryptNoteData(data, userId);
    
    // 마지막 접근 시간 업데이트
    await updateDoc(docRef, {
      lastAccessedAt: serverTimestamp()
    });
    
    return { id: docSnap.id, ...decryptedNote };
  } catch (error) {
    console.error('Error getting secure note:', error);
    throw error;
  }
}

/**
 * 메모 목록 조회
 */
export async function getSecureNotes(userId, filters = {}) {
  try {
    let q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId)
    );
    
    // 필터 적용
    if (filters.category) {
      q = query(q, where('category', '==', filters.category));
    }
    
    if (filters.isArchived !== undefined) {
      q = query(q, where('isArchived', '==', filters.isArchived));
    }
    
    // 정렬
    const sortBy = filters.sortBy || 'updatedAt';
    const sortOrder = filters.sortOrder || 'desc';
    q = query(q, orderBy(sortBy, sortOrder));
    
    const querySnapshot = await getDocs(q);
    const notes = [];
    
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      const decryptedNote = await decryptNoteData(data, userId);
      notes.push({ id: doc.id, ...decryptedNote });
    }
    
    return notes;
  } catch (error) {
    console.error('Error getting secure notes:', error);
    throw error;
  }
}

/**
 * 메모 업데이트
 */
export async function updateSecureNote(noteId, userId, updates) {
  try {
    const docRef = doc(db, COLLECTION_NAME, noteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Note not found');
    }
    
    if (docSnap.data().userId !== userId) {
      throw new Error('Unauthorized access');
    }
    
    // 암호화가 필요한 필드 처리
    const encryptedUpdates = { ...updates };
    
    if (updates.title) {
      encryptedUpdates.title = await encryptData(updates.title, userId);
    }
    
    if (updates.content) {
      encryptedUpdates.content = await encryptData(updates.content, userId);
    }
    
    if (updates.plainTextContent) {
      encryptedUpdates.plainTextContent = await encryptData(updates.plainTextContent, userId);
    }
    
    if (updates.tags) {
      encryptedUpdates.tags = await encryptData(JSON.stringify(updates.tags), userId);
    }
    
    if (updates.attachments) {
      encryptedUpdates.attachments = updates.attachments.length > 0 
        ? await Promise.all(
            updates.attachments.map(async (att) => ({
              ...att,
              name: await encryptData(att.name, userId)
            }))
          )
        : [];
    }
    
    encryptedUpdates.updatedAt = serverTimestamp();
    
    await updateDoc(docRef, encryptedUpdates);
    
    // 업데이트된 문서를 다시 가져와서 실제 타임스탬프 값 얻기
    const updatedDoc = await getDoc(docRef);
    const updatedData = updatedDoc.data();
    
    // 업데이트된 메모를 복호화하여 반환
    const decryptedUpdates = await decryptNoteData(updatedData, userId);
    return { id: noteId, ...decryptedUpdates };
  } catch (error) {
    console.error('Error updating secure note:', error);
    throw error;
  }
}

/**
 * 메모 삭제
 */
export async function deleteSecureNote(noteId, userId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, noteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Note not found');
    }
    
    const noteData = docSnap.data();
    
    if (noteData.userId !== userId) {
      throw new Error('Unauthorized access');
    }
    
    // Storage에서 첨부파일 삭제
    if (noteData.attachments && noteData.attachments.length > 0) {
      const { deleteNoteFiles } = await import('./storageService');
      const filePaths = noteData.attachments
        .filter(att => att.path)
        .map(att => att.path);
      
      if (filePaths.length > 0) {
        try {
          await deleteNoteFiles(filePaths);
          console.log(`Deleted ${filePaths.length} files from Storage`);
        } catch (storageError) {
          console.error('Error deleting files from Storage:', storageError);
          // Storage 삭제 실패해도 메모는 삭제 진행
        }
      }
    }
    
    // Firestore 문서 삭제
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting secure note:', error);
    throw error;
  }
}

/**
 * 메모 데이터 복호화 헬퍼
 */
async function decryptNoteData(data, userId) {
  try {
    const decrypted = { ...data };
    
    // title이 객체인 경우 (암호화된 경우) 복호화
    if (data.title && typeof data.title === 'object') {
      decrypted.title = await decryptData(data.title, userId);
    }
    
    // content가 객체인 경우 (암호화된 경우) 복호화
    if (data.content && typeof data.content === 'object') {
      decrypted.content = await decryptData(data.content, userId);
    }
    
    // plainTextContent가 객체인 경우 (암호화된 경우) 복호화
    if (data.plainTextContent && typeof data.plainTextContent === 'object') {
      try {
        decrypted.plainTextContent = await decryptData(data.plainTextContent, userId);
      } catch (error) {
        console.warn('Failed to decrypt plainTextContent:', error);
        decrypted.plainTextContent = '복호화 실패';
      }
    }
    
    // tags가 객체인 경우 (암호화된 경우) 복호화
    if (data.tags && typeof data.tags === 'object' && !Array.isArray(data.tags)) {
      const tagsStr = await decryptData(data.tags, userId);
      decrypted.tags = JSON.parse(tagsStr);
    }
    
    // 첨부파일 이름 복호화
    if (data.attachments && data.attachments.length > 0) {
      decrypted.attachments = await Promise.all(
        data.attachments.map(async (att) => ({
          ...att,
          name: att.name && typeof att.name === 'object' 
            ? await decryptData(att.name, userId) 
            : att.name
        }))
      );
    }
    
    return decrypted;
  } catch (error) {
    console.error('Error decrypting note data:', error);
    throw error;
  }
}

/**
 * 메모 검색
 */
export async function searchSecureNotes(userId, searchTerm) {
  try {
    const notes = await getSecureNotes(userId);
    
    if (!searchTerm) return notes;
    
    const lowerSearch = searchTerm.toLowerCase();
    
    return notes.filter(note => {
      const titleMatch = note.title?.toLowerCase().includes(lowerSearch);
      const contentMatch = note.plainTextContent?.toLowerCase().includes(lowerSearch);
      const tagMatch = note.tags?.some(tag => 
        tag.toLowerCase().includes(lowerSearch)
      );
      
      return titleMatch || contentMatch || tagMatch;
    });
  } catch (error) {
    console.error('Error searching secure notes:', error);
    throw error;
  }
}

/**
 * 즐겨찾기 토글
 */
export async function toggleNoteFavorite(noteId, userId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, noteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Note not found');
    }
    
    if (docSnap.data().userId !== userId) {
      throw new Error('Unauthorized access');
    }
    
    const currentFavorite = docSnap.data().isFavorite || false;
    
    await updateDoc(docRef, {
      isFavorite: !currentFavorite,
      updatedAt: serverTimestamp()
    });
    
    return !currentFavorite;
  } catch (error) {
    console.error('Error toggling favorite:', error);
    throw error;
  }
}

/**
 * 메모 아카이브
 */
export async function archiveNote(noteId, userId, isArchived = true) {
  try {
    await updateSecureNote(noteId, userId, { isArchived });
    return true;
  } catch (error) {
    console.error('Error archiving note:', error);
    throw error;
  }
}